import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';
import { Check, Loader2, ArrowRight } from 'lucide-react';
import ProducerAgreementStep from '../components/onboarding/ProducerAgreementStep.jsx';

/**
 * Onboarding step between Signup and Phone Setup. Signing continues to `?return=`
 * (default /phone-setup); an already-signed agent just sees a continue button.
 * @returns {JSX.Element}
 */
export default function ProducerAgreement() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const returnTo = params.get('return') || '/phone-setup';

  const { data, isLoading } = useQuery({
    queryKey: ['producer-agreement'],
    queryFn: () => apiGet('/onboarding/producer-agreement'),
  });

  const onSigned = async () => {
    await qc.invalidateQueries({ queryKey: ['producer-agreement'] });
    nav(returnTo, { replace: true });
  };

  return (
    <div className="space-y-4">
      <TopBar title="Producer Agreement" subtitle="Onboarding · Step 1 of 3" />
      {isLoading ? (
        <div className="panel p-8 flex items-center justify-center text-white/50">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading agreement…
        </div>
      ) : data?.alreadySigned ? (
        <div className="panel p-8 max-w-2xl mx-auto text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mx-auto">
            <Check className="w-7 h-7 text-emerald-300" />
          </div>
          <h2 className="text-xl font-semibold text-white">Your Producer Agreement is on file</h2>
          <button
            type="button"
            onClick={() => nav(returnTo)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-civix-purple/25 text-white border border-civix-purple/50 hover:bg-civix-purple/40"
          >
            Continue to Phone Setup <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <ProducerAgreementStep data={data} onSigned={onSigned} />
      )}
    </div>
  );
}
